import { ChevronDown } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { DynamicFlag, FlagUtils } from '@sankyu/react-circle-flags';
import type { Nullable, Optional } from '@silverhand/essentials';
import classNames from 'classnames';
import type { Ref, RefObject } from 'react';
import { useId, useMemo, useRef, useState } from 'react';

import { fieldButtonHitAreaClass } from '@/shared/components/InputFields/InputField';
import {
  getCountryList,
  getDefaultCountryCallingCode,
  getCountryByCallingCode,
} from '@/utils/country-code';

import CountryCodeDropdown from './CountryCodeDropdown';

type Props = {
  readonly className?: string;
  readonly value?: string;
  readonly inputRef?: RefObject<Optional<Nullable<HTMLInputElement>>>;
  /** The field label's id, so the trigger reads as "Phone number +91" rather than just "+91". */
  readonly labelId?: string;
  readonly isVisible?: boolean;
  readonly isInteractive?: boolean;
  readonly onChange?: (value: string) => void;
  readonly ref?: Ref<HTMLButtonElement>;
};

const CountryCodeSelector = ({
  className,
  value,
  inputRef,
  labelId,
  isVisible = true,
  isInteractive = true,
  onChange,
  ref,
}: Props) => {
  const buttonId = useId();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const anchorRef = useRef<Nullable<HTMLElement>>(null);
  const countryList = useMemo(getCountryList, []);
  const defaultCountCode = useMemo(getDefaultCountryCallingCode, []);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty code also falls back
  const countryCode = value || defaultCountCode;

  // `+1` maps to several countries; getCountryByCallingCode prefers the locale default.
  const isoCountryCode = useMemo(() => getCountryByCallingCode(countryCode), [countryCode]);

  const showDropDown = () => {
    // The popover anchors to the whole field (the input's group), not to this small trigger.
    // eslint-disable-next-line @silverhand/fp/no-mutation -- a ref write, not render state
    anchorRef.current = inputRef?.current?.parentElement ?? null;
    // Drops the on-screen keyboard before the country sheet opens over it.
    inputRef?.current?.blur();
    setIsDropdownOpen(true);
  };

  return (
    <CountryCodeDropdown
      inputRef={inputRef}
      anchorRef={anchorRef}
      isOpen={isDropdownOpen}
      countryCode={countryCode}
      countryList={countryList}
      onClose={() => {
        setIsDropdownOpen(false);
      }}
      onChange={onChange}
    >
      <Button
        ref={ref}
        id={buttonId}
        size="sm"
        variant="ghost"
        aria-labelledby={labelId ? `${labelId} ${buttonId}` : undefined}
        aria-haspopup="dialog"
        aria-expanded={isDropdownOpen}
        data-testid="country-code-selector"
        isDisabled={!isInteractive || !isVisible}
        className={classNames(
          'gap-1.5 px-2 font-medium tabular-nums',
          fieldButtonHitAreaClass,
          className
        )}
        onPress={showDropDown}
      >
        {isoCountryCode && FlagUtils.isValidCountryCode(isoCountryCode) && (
          <DynamicFlag
            code={isoCountryCode.toLowerCase()}
            width={20}
            height={20}
            aria-hidden="true"
            className="size-5 shrink-0 rounded-full"
          />
        )}
        <span dir="ltr">{`+${countryCode}`}</span>
        <ChevronDown
          aria-hidden
          className={classNames(
            'size-3.5 shrink-0 text-muted transition-transform duration-(--duration-fast) ease-(--ease-smooth-out) motion-reduce:transition-none',
            isDropdownOpen && 'rotate-180'
          )}
        />
      </Button>
    </CountryCodeDropdown>
  );
};

export default CountryCodeSelector;
